import { useMemo, useState } from 'react';

import { Box, Stack } from '@mui/material';
import { TZDate } from 'react-day-picker';

import { PeriodComparisonPickerBodyProps } from './_types';
import { PeriodComparisonInfinitePicker } from './PeriodComparisonInfinitePicker';
import { PeriodComparisonPickerInput } from './PeriodComparisonPickerInput';
import { useDatePickerContext } from '../../components/useDatePickerContext';
import { PeriodComparisonPickerProviderProps } from '../../types';
import {
  utcToDate,
  utcNow,
  rangeToUtc,
  buildDisabledMatcher,
} from '../../utils';

export const PeriodComparisonPickerBody = ({
  label,
  variant,
  hideRangeInputs,
  initialRange,
  otherRange,
  periodProps,
}: PeriodComparisonPickerBodyProps) => {
  const ctx = useDatePickerContext<PeriodComparisonPickerProviderProps>();

  const [initialMonth] = useState<TZDate>(
    utcToDate(initialRange ? initialRange : utcNow(), ctx.timezone)
  );

  const [startPlaceholder, setStartPlaceholder] = useState('');
  const [endPlaceholder, setEndPlaceholder] = useState('');

  const disabled = useMemo(() => {
    const rangeInUtc = rangeToUtc(periodProps.selected);
    const otherRangeInUtc = rangeToUtc(otherRange);

    const { minDate, maxDate, fullDisabling } =
      (typeof periodProps.dateBoundaries === 'function'
        ? periodProps.dateBoundaries({
            range: rangeInUtc,
            otherRange: otherRangeInUtc,
            timezone: ctx.timezone,
            selectedPeriod: ctx.activePeriod,
          })
        : periodProps.dateBoundaries) || {};

    return buildDisabledMatcher({
      minDate,
      maxDate,
      timezone: ctx.timezone,
      fullDisabling,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodProps.selected, otherRange, ctx.timezone]);

  const startMonth = useMemo(() => {
    const startMonth =
      typeof periodProps.startMonth === 'function'
        ? periodProps.startMonth({
            range: rangeToUtc(periodProps.selected),
            otherRange: rangeToUtc(otherRange),
            timezone: ctx.timezone,
          })
        : periodProps.startMonth;

    if (startMonth) {
      return new TZDate(startMonth, ctx.timezone);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodProps.selected, otherRange, ctx.timezone]);

  const endMonth = useMemo(() => {
    const endMonth =
      typeof periodProps.endMonth === 'function'
        ? periodProps.endMonth({
            range: rangeToUtc(periodProps.selected),
            otherRange: rangeToUtc(otherRange),
            timezone: ctx.timezone,
          })
        : periodProps.endMonth;

    if (endMonth) {
      return new TZDate(endMonth, ctx.timezone);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodProps.selected, otherRange, ctx.timezone]);

  return (
    <Stack gap={8} data-testid={`${label}-body`}>
      {!hideRangeInputs && (
        <Box px={24}>
          <PeriodComparisonPickerInput
            label={label}
            selectedRange={periodProps.selected}
            setSelectedRange={periodProps.setSelected}
            endPlaceholder={endPlaceholder}
            startPlaceholder={startPlaceholder}
            dateBoundaries={periodProps.dateBoundaries}
            otherRange={otherRange}
            pickerRef={periodProps.pickerRef}
          />
        </Box>
      )}

      <PeriodComparisonInfinitePicker
        label={label}
        selectedRange={periodProps.selected}
        setSelectedRange={periodProps.setSelected}
        setEndPlaceholder={setEndPlaceholder}
        setStartPlaceholder={setStartPlaceholder}
        infinitePickerProps={{
          variant: variant,
          initialMonth: initialMonth,
          startMonth: startMonth,
          endMonth: endMonth,
        }}
        otherRange={otherRange}
        disabledDates={disabled}
        setPickerRef={periodProps.setPickerRef}
      />
    </Stack>
  );
};
